/**
 * Schema converter module.
 *
 * Converts `ToolRevisionData` (from the existing RTK Query hook) into
 * `SchemaSummary` format expected by the pre-populate API. This runs
 * entirely client-side — no backend call needed.
 */

import { ToolRevisionData, ToolParameterSchema } from 'src/store/interfaces/Tool.types';
import {
  ParameterSummary,
  SchemaSummary,
  SchemaDerivationResult,
  ToolConfig,
  MetricsSelection,
  DimensionsSelection,
  ColumnMapping,
  LLMPolish,
  GroupExpansionEntry,
  TemplateDefinition,
} from './types';

/**
 * Convert a `ToolRevisionData` object into a `SchemaSummary`.
 *
 * Maps each entry in `revision.inputSchema` to a `ParameterSummary`,
 * preserving name, type, default, enum, and examples. The `required`
 * flag is derived from `revision.required`.
 */
export function convertToSchemaSummary(
  revision: ToolRevisionData,
): SchemaSummary {
  const parameters: Record<string, ParameterSummary> = {};

  for (const [key, param] of Object.entries(revision.inputSchema)) {
    parameters[key] = {
      name: param.parameterName,
      type: param.type,
      required: revision.required?.includes(key) ?? false,
      default: param.default ?? null,
      enum: param.enum ?? null,
      examples: param.examples ?? null,
    };
  }

  return {
    tool_name: revision.toolName,
    parameters,
    required_params: revision.required ?? [],
  };
}

/**
 * Serialize a `SchemaSummary` into a YAML-like string for the
 * pre-populate API's `schema_content` field.
 *
 * The output is a human-readable representation that the backend
 * `LLMPrePopulator` can parse to understand the tool's parameter
 * structure.
 */
export function serializeSchemaSummary(summary: SchemaSummary): string {
  const lines: string[] = [];

  lines.push(`tool_name: ${summary.tool_name}`);
  lines.push('');
  lines.push('input_schema:');

  for (const [key, param] of Object.entries(summary.parameters)) {
    lines.push(`  ${key}:`);
    lines.push(`    parameterName: ${param.name}`);
    lines.push(`    type: ${param.type}`);

    if (param.required) {
      lines.push('    required: true');
    }

    if (param.default !== null && param.default !== undefined) {
      lines.push(`    default: ${JSON.stringify(param.default)}`);
    }

    if (param.enum !== null && param.enum.length > 0) {
      lines.push(`    enum: ${JSON.stringify(param.enum)}`);
    }

    if (param.examples !== null && param.examples.length > 0) {
      lines.push(`    examples: ${JSON.stringify(param.examples)}`);
    }
  }

  if (summary.required_params.length > 0) {
    lines.push('');
    lines.push(`required: ${JSON.stringify(summary.required_params)}`);
  }

  return lines.join('\n');
}

// ─── Schema Derivation Constants ───────────────────────────────────────

/** Exclusion set for dimensions selection (lowercased keys to exclude). */
const DIMENSIONS_EXCLUSION_SET = ['metrics', 'limit', 'orderby', 'dimensions'];

/** Exclusion set for frequency weight columns (lowercased keys to exclude). */
const FREQUENCY_WEIGHT_EXCLUSION_SET = ['metrics', 'limit', 'orderby', 'dimensions', 'scenario'];

/** Exclusion set for stratify columns (lowercased keys to exclude). */
const STRATIFY_EXCLUSION_SET = ['metrics', 'limit', 'orderby', 'dimensions'];

/** Default evaluation criteria for tool config. */
const DEFAULT_EVALUATION_CRITERIA = 'PNL_DEFAULT';

/** Default allowed additional tools for tool config. */
const DEFAULT_ALLOWED_ADDITIONAL_TOOLS = ['execute_python'];

/** Default column mapping metrics value. */
const DEFAULT_COLUMN_MAPPING_METRICS = 'derived_metrics_available';

/** Minimum enum size for frequency weight column eligibility. */
const FREQUENCY_WEIGHT_MIN_ENUM_SIZE = 3;

/** Minimum enum size for stratify column eligibility. */
const STRATIFY_MIN_ENUM_SIZE = 5;

/** Maximum number of stratify columns to return. */
const STRATIFY_MAX_COLUMNS = 3;

/** Default dimensions selection probability. */
const DIMENSIONS_DEFAULT_PROBABILITY = 0.4;

/** Default dimensions selection min values. */
const DIMENSIONS_DEFAULT_MIN_VALUES = 1;

/** Default dimensions selection max values. */
const DIMENSIONS_DEFAULT_MAX_VALUES = 2;

/** Default group expansion weight. */
const GROUP_EXPANSION_DEFAULT_WEIGHT = 0.3;

// ─── Schema Derivation Engine ──────────────────────────────────────────

/**
 * Finds the first key in inputSchema that contains "date" (case-insensitive).
 * Returns the key or empty string if none found.
 */
function findTemporalParameterKey(
  inputSchema: Record<string, ToolParameterSchema>,
): string {
  for (const key of Object.keys(inputSchema)) {
    if (key.toLowerCase().includes('date')) {
      return key;
    }
  }
  return '';
}

/**
 * Derives tool configuration from revision data.
 *
 * - `schema` = `{toolName}_schema.yaml`
 * - `temporal_parameter_name` = first key containing "date" (case-insensitive)
 * - `evaluation_criteria` = "PNL_DEFAULT"
 * - `allowed_additional_tools` = ["execute_python"]
 */
export function deriveToolConfig(
  revision: ToolRevisionData,
): Record<string, ToolConfig> {
  const temporalKey = findTemporalParameterKey(revision.inputSchema);

  const toolConfig: ToolConfig = {
    schema: `${revision.toolName}_schema.yaml`,
    temporal_parameter_name: temporalKey,
    evaluation_criteria: DEFAULT_EVALUATION_CRITERIA,
    allowed_additional_tools: [...DEFAULT_ALLOWED_ADDITIONAL_TOOLS],
  };

  return { [revision.toolName]: toolConfig };
}

/**
 * Derives metrics selection from the inputSchema.
 *
 * Finds the "metrics" key and extracts its enum field.
 * If no "metrics" key or null enum, returns empty enum array.
 * Sets `coupled_with_other_params: true`.
 */
export function deriveMetricsSelection(
  inputSchema: Record<string, ToolParameterSchema>,
): MetricsSelection {
  const metricsParam = inputSchema['metrics'];
  const enumValues = metricsParam?.enum ?? [];

  return {
    coupled_with_other_params: true,
    enum: enumValues,
  };
}

/**
 * Derives dimensions selection from the inputSchema.
 *
 * Selects array-type params whose keys (lowercased) are NOT in the
 * exclusion set. Populates enum with the keys of eligible parameters.
 */
export function deriveDimensionsSelection(
  inputSchema: Record<string, ToolParameterSchema>,
): DimensionsSelection {
  const eligibleKeys: string[] = [];

  for (const [key, param] of Object.entries(inputSchema)) {
    if (
      param.type === 'array' &&
      !DIMENSIONS_EXCLUSION_SET.includes(key.toLowerCase())
    ) {
      eligibleKeys.push(key);
    }
  }

  return {
    enabled: true,
    probability: DIMENSIONS_DEFAULT_PROBABILITY,
    min_values: DIMENSIONS_DEFAULT_MIN_VALUES,
    max_values: DIMENSIONS_DEFAULT_MAX_VALUES,
    enum: eligibleKeys,
  };
}

/**
 * Derives schema required defaults from inputSchema and required array.
 *
 * For each key in `required`, includes `key → default` if default is
 * non-null and non-undefined. Omits parameters with null/undefined
 * defaults or not in `required`.
 */
export function deriveSchemaRequiredDefaults(
  inputSchema: Record<string, ToolParameterSchema>,
  required: string[],
): Record<string, unknown> {
  const defaults: Record<string, unknown> = {};

  for (const key of required) {
    const param = inputSchema[key];
    if (param && param.default !== null && param.default !== undefined) {
      defaults[key] = param.default;
    }
  }

  return defaults;
}

/**
 * Derives parameter name mapping from inputSchema.
 *
 * For each parameter where `key !== param.parameterName`, includes
 * `key → parameterName`. Excludes entries where key equals parameterName.
 */
export function deriveParamNameMapping(
  inputSchema: Record<string, ToolParameterSchema>,
): Record<string, string> {
  const mapping: Record<string, string> = {};

  for (const [key, param] of Object.entries(inputSchema)) {
    if (key !== param.parameterName) {
      mapping[key] = param.parameterName;
    }
  }

  return mapping;
}

/**
 * Derives column mapping from inputSchema.
 *
 * If a temporal param key is found (first key containing "date"):
 *   min_date = "min_{key}", max_date = "max_{key}"
 * If not found:
 *   min_date = "min_date", max_date = "max_date"
 * Always: metrics = "derived_metrics_available"
 */
export function deriveColumnMapping(
  inputSchema: Record<string, ToolParameterSchema>,
): ColumnMapping {
  const temporalKey = findTemporalParameterKey(inputSchema);

  if (temporalKey) {
    return {
      min_date: `min_${temporalKey}`,
      max_date: `max_${temporalKey}`,
      metrics: DEFAULT_COLUMN_MAPPING_METRICS,
    };
  }

  return {
    min_date: 'min_date',
    max_date: 'max_date',
    metrics: DEFAULT_COLUMN_MAPPING_METRICS,
  };
}

/**
 * Derives frequency weight columns from inputSchema.
 *
 * Finds the first array-type param with enum > 3 values, excluding
 * keys (lowercased) in the frequency weight exclusion set.
 * Returns [key] or empty array if none found.
 */
export function deriveFrequencyWeightColumns(
  inputSchema: Record<string, ToolParameterSchema>,
): string[] {
  for (const [key, param] of Object.entries(inputSchema)) {
    if (
      param.type === 'array' &&
      param.enum &&
      param.enum.length > FREQUENCY_WEIGHT_MIN_ENUM_SIZE &&
      !FREQUENCY_WEIGHT_EXCLUSION_SET.includes(key.toLowerCase())
    ) {
      return [key];
    }
  }

  return [];
}

/**
 * Derives group expansions from inputSchema by scanning parameter
 * descriptions for group patterns.
 *
 * Pattern: "X" includes "A", "B", "C" or similar structured text.
 * Creates entries with label, expands_to, weight: 0.3.
 * Returns empty object if no patterns found.
 * Must NOT hardcode any parameter names.
 */
export function deriveGroupExpansions(
  inputSchema: Record<string, ToolParameterSchema>,
): Record<string, GroupExpansionEntry[]> {
  const result: Record<string, GroupExpansionEntry[]> = {};

  // Regex to match patterns like: "GroupLabel" includes "A", "B", "C"
  // Captures the label and the entire values portion after "includes"
  const expandedPattern =
    /"([^"]+)"\s+includes?\s+((?:"[^"]+"(?:\s*,\s*|\s+and\s+|\s*)?)+)/gi;

  const quotedValuePattern = /"([^"]+)"/g;

  for (const [key, param] of Object.entries(inputSchema)) {
    if (!param.description) {
      continue;
    }

    const entries: GroupExpansionEntry[] = [];
    let match: RegExpExecArray | null;

    // Reset regex state
    expandedPattern.lastIndex = 0;

    while ((match = expandedPattern.exec(param.description)) !== null) {
      const label = match[1];
      const valuesStr = match[2];

      // Extract all quoted values from the values portion
      const expandsTo: string[] = [];
      let valueMatch: RegExpExecArray | null;
      quotedValuePattern.lastIndex = 0;

      while ((valueMatch = quotedValuePattern.exec(valuesStr)) !== null) {
        expandsTo.push(valueMatch[1]);
      }

      if (expandsTo.length > 0) {
        entries.push({
          label,
          expands_to: expandsTo,
          weight: GROUP_EXPANSION_DEFAULT_WEIGHT,
        });
      }
    }

    if (entries.length > 0) {
      result[key] = entries;
    }
  }

  return result;
}

/**
 * Derives LLM polish removable values from schema required defaults.
 *
 * - Extracts string defaults as removable values
 * - Extracts first element from single-element array defaults
 * - Does NOT include multi-element arrays, objects, or non-string/non-array defaults
 * - Sets must_preserve: []
 */
export function deriveLLMPolishRemovableValues(
  schemaRequiredDefaults: Record<string, unknown>,
): LLMPolish {
  const removableValues: string[] = [];

  for (const value of Object.values(schemaRequiredDefaults)) {
    if (typeof value === 'string') {
      removableValues.push(value);
    } else if (Array.isArray(value) && value.length === 1) {
      const firstElement = value[0];
      if (typeof firstElement === 'string') {
        removableValues.push(firstElement);
      }
    }
    // Skip multi-element arrays, objects, booleans, numbers, etc.
  }

  return {
    removable_values: removableValues,
    must_preserve: [],
  };
}

/**
 * Derives stratify columns from inputSchema.
 *
 * Finds array-type params with enum > 5 values, excluding keys
 * (lowercased) in the stratify exclusion set.
 * Returns top 2-3 keys sorted by enum length descending (max 3).
 */
export function deriveStratifyColumns(
  inputSchema: Record<string, ToolParameterSchema>,
): string[] {
  const candidates: Array<{ key: string; enumLength: number }> = [];

  for (const [key, param] of Object.entries(inputSchema)) {
    if (
      param.type === 'array' &&
      param.enum &&
      param.enum.length > STRATIFY_MIN_ENUM_SIZE &&
      !STRATIFY_EXCLUSION_SET.includes(key.toLowerCase())
    ) {
      candidates.push({ key, enumLength: param.enum.length });
    }
  }

  // Sort by enum length descending
  candidates.sort((a, b) => b.enumLength - a.enumLength);

  // Return top 3 keys
  return candidates.slice(0, STRATIFY_MAX_COLUMNS).map((c) => c.key);
}

/**
 * Derives all possible config values from a ToolRevisionData object.
 * Pure function — no side effects, no API calls, deterministic output.
 *
 * Composes all helper functions into a single derivation result.
 * Handles edge cases: empty inputSchema, missing required array, missing fields.
 */
export function deriveConfigFromSchema(
  revision: ToolRevisionData,
): SchemaDerivationResult {
  const inputSchema = revision.inputSchema ?? {};
  const required = revision.required ?? [];

  const tools = deriveToolConfig(revision);

  // Templates are NOT populated by schema derivation — they remain empty
  // until the user clicks "Generate Config" and the LLM populates them
  // from root questions + business context.
  const templates: Record<string, Array<TemplateDefinition & { enabled: boolean }>> = {};

  const metricsSelection = deriveMetricsSelection(inputSchema);
  const dimensionsSelection = deriveDimensionsSelection(inputSchema);
  const schemaRequiredDefaults = deriveSchemaRequiredDefaults(inputSchema, required);
  const paramNameMapping = deriveParamNameMapping(inputSchema);
  const columnMapping = deriveColumnMapping(inputSchema);
  const frequencyWeightColumns = deriveFrequencyWeightColumns(inputSchema);
  const groupExpansion = deriveGroupExpansions(inputSchema);
  const llmPolish = deriveLLMPolishRemovableValues(schemaRequiredDefaults);
  const stratifyColumns = deriveStratifyColumns(inputSchema);

  return {
    tools,
    templates,
    metricsSelection,
    dimensionsSelection,
    schemaRequiredDefaults,
    paramNameMapping,
    columnMapping,
    frequencyWeightColumns,
    groupExpansion,
    llmPolish,
    stratifyColumns,
  };
}
